<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import {
    applyFirmBulkAssignee,
    previewFirmBulkAssignee,
  } from '@entities/firm/store.svelte';
  import type { SimpleOption } from '@shared/constants/choices';
  import type {
    FirmBulkAssigneeRequest,
    FirmBulkAssigneeResult,
    FirmListQuery,
  } from '@shared/types/firm';

  type SelectionMode = 'ids' | 'query';
  const DEFAULT_ALLOWED_SELECTION_MODES: SelectionMode[] = ['ids', 'query'];

  interface Props {
    open: boolean;
    initialSelectionMode: SelectionMode;
    allowedSelectionModes?: SelectionMode[];
    selectedIds: string[];
    total: number;
    query: FirmListQuery;
    accountOptions: SimpleOption[];
    accountLabelOptions: SimpleOption[];
    onClose: () => void;
    onDone: (result: FirmBulkAssigneeResult) => void;
  }

  let {
    open,
    initialSelectionMode,
    allowedSelectionModes = DEFAULT_ALLOWED_SELECTION_MODES,
    selectedIds,
    total,
    query,
    accountOptions,
    accountLabelOptions,
    onClose,
    onDone,
  }: Props = $props();

  let selectionMode = $state<SelectionMode>('ids');
  let targetAssigneeAccountId = $state('');
  let onlyUnassigned = $state(false);
  let previewing = $state(false);
  let executing = $state(false);
  let serverError = $state<string | null>(null);
  let preview = $state<FirmBulkAssigneeResult | null>(null);
  let previewSignature = $state<string | null>(null);

  const numberFormatter = new Intl.NumberFormat('ja-JP');
  const accountLabelById = $derived(
    new Map(accountLabelOptions.map((option) => [option.value, option.label])),
  );

  const request = $derived(buildRequest());
  const requestSignature = $derived(request ? JSON.stringify(request) : null);
  const previewIsCurrent = $derived(
    preview !== null &&
      previewSignature !== null &&
      previewSignature === requestSignature,
  );
  const targetCount = $derived(
    selectionMode === 'ids' ? selectedIds.length : total,
  );
  const idsSelectionAllowed = $derived(allowedSelectionModes.includes('ids'));
  const querySelectionAllowed = $derived(
    allowedSelectionModes.includes('query'),
  );
  const idsSelectionDisabled = $derived(
    previewing ||
      executing ||
      selectedIds.length === 0 ||
      !idsSelectionAllowed,
  );
  const querySelectionDisabled = $derived(
    previewing || executing || total === 0 || !querySelectionAllowed,
  );
  const selectedCountLabel = $derived(formatCount(selectedIds.length));
  const totalCountLabel = $derived(formatCount(total));
  const targetCountLabel = $derived(formatCount(targetCount));
  const canPreview = $derived(request !== null && !previewing && !executing);
  const canExecute = $derived(previewIsCurrent && !previewing && !executing);

  $effect(() => {
    if (!open) return;
    selectionMode = initialMode();
    targetAssigneeAccountId = '';
    onlyUnassigned = false;
    previewing = false;
    executing = false;
    serverError = null;
    preview = null;
    previewSignature = null;
  });

  function initialMode(): SelectionMode {
    if (
      initialSelectionMode === 'ids' &&
      idsSelectionAllowed &&
      selectedIds.length > 0
    ) {
      return 'ids';
    }
    if (
      initialSelectionMode === 'query' &&
      querySelectionAllowed &&
      total > 0
    ) {
      return 'query';
    }
    if (idsSelectionAllowed && selectedIds.length > 0) return 'ids';
    if (querySelectionAllowed && total > 0) return 'query';
    return 'ids';
  }

  function queryWithoutPaging(value: FirmListQuery): FirmListQuery {
    const next = { ...value };
    delete next.page;
    delete next.page_size;
    return next;
  }

  function formatCount(value: number): string {
    return numberFormatter.format(value);
  }

  function buildRequest(): FirmBulkAssigneeRequest | null {
    if (targetAssigneeAccountId === '') return null;
    if (selectionMode === 'ids') {
      if (!idsSelectionAllowed || selectedIds.length === 0) return null;
      return {
        selection: { mode: 'ids', ids: selectedIds },
        target_assignee_account_id: targetAssigneeAccountId,
        only_unassigned: onlyUnassigned,
      };
    }
    if (!querySelectionAllowed || total === 0) return null;
    return {
      selection: { mode: 'query', query: queryWithoutPaging(query) },
      target_assignee_account_id: targetAssigneeAccountId,
      only_unassigned: onlyUnassigned,
    };
  }

  function assigneeLabel(accountId: string | null): string {
    if (!accountId) return '未担当';
    return accountLabelById.get(accountId) ?? accountId;
  }

  function close(): void {
    if (previewing || executing) return;
    onClose();
  }

  function changeSelectionMode(mode: SelectionMode): void {
    if (mode === 'ids' && idsSelectionDisabled) return;
    if (mode === 'query' && querySelectionDisabled) return;
    selectionMode = mode;
  }

  async function handlePreview(ev: SubmitEvent): Promise<void> {
    ev.preventDefault();
    if (!request || previewing || executing) return;
    previewing = true;
    serverError = null;
    try {
      const result = await previewFirmBulkAssignee(request);
      preview = result;
      previewSignature = requestSignature;
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
      preview = null;
      previewSignature = null;
    } finally {
      previewing = false;
    }
  }

  async function handleExecute(): Promise<void> {
    if (!request || !canExecute || executing) return;
    executing = true;
    serverError = null;
    try {
      const result = await applyFirmBulkAssignee(request);
      onDone(result);
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      executing = false;
    }
  }
</script>

<Modal {open} title="担当者を一括割り当て" onClose={close}>
  <form id="firm-bulk-assignee-form" onsubmit={handlePreview}>
    <fieldset>
      <legend>対象</legend>
      <label class="option">
        <input
          type="radio"
          name="selection-mode"
          value="ids"
          checked={selectionMode === 'ids'}
          disabled={idsSelectionDisabled}
          onchange={() => changeSelectionMode('ids')}
        />
        <span>選択した事務所 {selectedCountLabel}件</span>
      </label>
      <label class="option">
        <input
          type="radio"
          name="selection-mode"
          value="query"
          checked={selectionMode === 'query'}
          disabled={querySelectionDisabled}
          onchange={() => changeSelectionMode('query')}
        />
        <span>現在の検索条件に一致する全{totalCountLabel}件</span>
      </label>
    </fieldset>

    <p class="bulk-caution">
      大量更新になるため、実行前にプレビューで件数と対象を確認します。
    </p>

    <label class="field">
      <span class="field-label">担当者</span>
      <select
        bind:value={targetAssigneeAccountId}
        disabled={previewing || executing}
        required
      >
        <option value="">選択してください</option>
        {#each accountOptions as account (account.value)}
          <option value={account.value}>{account.label}</option>
        {/each}
      </select>
      {#if accountOptions.length === 0}
        <span class="hint">割り当て可能な担当者がありません。</span>
      {/if}
    </label>

    <label class="checkbox-option">
      <input
        type="checkbox"
        bind:checked={onlyUnassigned}
        disabled={previewing || executing}
      />
      <span>未担当の法律事務所だけに割り当てる</span>
    </label>

    {#if preview}
      <section class="preview" class:stale={!previewIsCurrent}>
        <header class="preview-head">
          <h4>プレビュー</h4>
          {#if !previewIsCurrent}
            <span>条件が変更されています</span>
          {/if}
        </header>
        <dl class="counts">
          <div>
            <dt>対象</dt>
            <dd>{preview.matched_count}</dd>
          </div>
          <div>
            <dt>更新</dt>
            <dd>{preview.update_count}</dd>
          </div>
          <div>
            <dt>変更なし</dt>
            <dd>{preview.unchanged_count}</dd>
          </div>
          <div>
            <dt>更新されない</dt>
            <dd>{preview.skipped_count}</dd>
          </div>
        </dl>
        {#if preview.sample.length > 0}
          <div class="sample">
            <p>対象サンプル</p>
            <ul>
              {#each preview.sample as item (item.id)}
                <li>
                  <span>{item.label}</span>
                  <small>{assigneeLabel(item.current_assignee_account_id)}</small>
                </li>
              {/each}
            </ul>
          </div>
        {/if}
      </section>
    {/if}

    {#if preview && !previewIsCurrent}
      <p class="warn">担当者・対象・未担当のみの条件を変更したため、再プレビューが必要です。</p>
    {/if}
    {#if serverError}<p class="server-error">{serverError}</p>{/if}
    <p class="hint">
      実行対象は {targetCountLabel} 件です。ページ上の表示件数ではなく、選択した対象種別で判定します。
    </p>
  </form>

  {#snippet footer()}
    <button type="button" onclick={close} disabled={previewing || executing}>
      キャンセル
    </button>
    <button type="submit" form="firm-bulk-assignee-form" disabled={!canPreview}>
      {previewing ? '確認中…' : 'プレビュー'}
    </button>
    <button
      type="button"
      class="primary"
      onclick={handleExecute}
      disabled={!canExecute}
    >
      {executing ? '実行中…' : 'プレビュー内容で実行'}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  fieldset {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 12px;
  }
  legend,
  .field-label {
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 700;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .option,
  .checkbox-option {
    align-items: center;
    display: flex;
    gap: 8px;
  }
  .option input,
  .checkbox-option input {
    flex: 0 0 auto;
  }
  select {
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    padding: 8px 10px;
  }
  .preview {
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    background: #eff6ff;
    padding: 12px;
  }
  .preview.stale {
    background: #fffbeb;
    border-color: #fcd34d;
  }
  .preview-head {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .preview-head h4 {
    font-size: 0.875rem;
    margin: 0;
  }
  .preview-head span {
    color: #92400e;
    font-size: 0.75rem;
    font-weight: 700;
  }
  .counts {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 0;
  }
  .counts div {
    background: #ffffff;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    padding: 8px;
    text-align: center;
  }
  .counts dt {
    color: #6b7280;
    font-size: 0.75rem;
  }
  .counts dd {
    font-size: 1.125rem;
    font-weight: 700;
    margin: 2px 0 0;
  }
  .sample {
    margin-top: 10px;
  }
  .sample p {
    color: #374151;
    font-size: 0.75rem;
    font-weight: 700;
    margin: 0 0 6px;
  }
  .sample ul {
    display: flex;
    flex-direction: column;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .sample li {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: space-between;
  }
  .sample small {
    color: #6b7280;
    flex: 0 0 auto;
  }
  .hint {
    color: #6b7280;
    font-size: 0.75rem;
    margin: 0;
  }
  .warn {
    color: #92400e;
    font-size: 0.875rem;
    margin: 0;
  }
  .bulk-caution {
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 6px;
    color: #92400e;
    font-size: 0.8125rem;
    margin: 0;
    padding: 8px 10px;
  }
  .server-error {
    color: #b91c1c;
    margin: 0;
  }
  button {
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
    padding: 8px 14px;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
  }
  button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  @media (max-width: 640px) {
    .counts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .sample li {
      align-items: flex-start;
      flex-direction: column;
      gap: 2px;
    }
  }
</style>
